import { motion } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import { ScrollReveal } from "@/components/shared/ScrollReveal";
import { useLang } from "@/i18n/LanguageContext";
import { magazines } from "@/i18n/translations";

// Category → cinematic cover image (shown on hover)
const CAT_IMG = {
  News: `${process.env.PUBLIC_URL}/images/magazines/newsmondo.jpg`,
  Food: `${process.env.PUBLIC_URL}/images/magazines/primochef.jpg`,
  Automotive: `${process.env.PUBLIC_URL}/images/magazines/mondo-motori.jpg`,
  Entertainment: `${process.env.PUBLIC_URL}/images/magazines/popcorntv.jpg`,
  Lifestyle: `${process.env.PUBLIC_URL}/images/magazines/donnaglamour.jpg`,
  Business: `${process.env.PUBLIC_URL}/images/magazines/economiafinanzaonline.jpg`,
  Sport: `${process.env.PUBLIC_URL}/images/magazines/notiziemilan.webp`,
  Benessere: `${process.env.PUBLIC_URL}/images/magazines/notiziebenessere.jpg`,
  Travel: `${process.env.PUBLIC_URL}/images/magazines/viaggiaretravel.jpg`,
};

function prettyDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export default function NetworkGrid({ showHeader = true }) {
  const { t, lang } = useLang();
  return (
    <section
      id="network"
      data-testid="network-section"
      className="relative bg-[#050505] text-white py-28 md:py-40 border-t border-white/10 scroll-mt-20"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {showHeader && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
            <ScrollReveal className="lg:col-span-7">
              <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-6">{t.network.eyebrow}</p>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight whitespace-pre-line">
                {t.network.title}
              </h2>
            </ScrollReveal>
            <ScrollReveal className="lg:col-span-5 lg:pb-2" delay={0.15}>
              <p className="text-neutral-400 text-lg leading-relaxed max-w-lg">{t.network.subtitle}</p>
            </ScrollReveal>
          </div>
        )}

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {magazines.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8% 0px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: (i % 6) * 0.05 }}
              className="tile-card spotlight-parent group relative h-64 overflow-hidden"
            >
              {/* Cover image — reveals on scroll */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 + (i % 3) * 0.1 }}
                className="absolute inset-0 pointer-events-none"
              >
                <img
                  src={m.img || CAT_IMG[m.cat] || CAT_IMG.News}
                  alt=""
                  className="w-full h-full object-cover spotlight-img group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-black/70" />
              </motion.div>

              <a
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${m.name} — apri il sito`}
                data-testid={`magazine-card-${m.name}`}
                className="absolute inset-0 p-6 md:p-7 flex flex-col justify-between"
              >
                <div className="relative flex items-center justify-between">
                  <span className="font-mono-eyebrow text-neutral-300 group-hover:text-[var(--dp-accent)] transition-colors">
                    {t.categories?.[m.cat] || m.cat}
                  </span>
                  <span className="w-9 h-9 rounded-full border border-white/25 flex items-center justify-center text-neutral-200 group-hover:bg-white group-hover:text-black group-hover:border-white transition-all duration-500">
                    <ArrowUpRight size={15} />
                  </span>
                </div>

                <div className="relative">
                  <h3 className="font-display text-2xl md:text-[30px] leading-none tracking-tight mb-2 text-white">
                    {m.name}
                  </h3>
                  <p className="text-neutral-200 text-[13px] leading-relaxed mb-4 line-clamp-2">
                    {typeof m.desc === "string" ? m.desc : m.desc[lang]}
                  </p>
                  <div className="flex items-center justify-between pt-3 border-t border-white/20">
                    <span className="text-[11px] tracking-widest lowercase text-neutral-300 group-hover:text-white transition-colors">
                      {prettyDomain(m.url)}
                    </span>
                  </div>
                </div>
              </a>

              {m.mediaKit && (
                <a
                  href={m.mediaKit}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`magazine-mediakit-${m.name}`}
                  aria-label={`${m.name} — scarica media kit`}
                  className="absolute bottom-5 right-5 z-20 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-white/25 bg-black/60 backdrop-blur-sm text-[10px] tracking-widest uppercase text-white hover:bg-[var(--dp-accent)] hover:border-[var(--dp-accent)] transition-all duration-500"
                >
                  <Download size={12} /> {t.network.mediaKit}
                </a>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
